import { ModalConfirmation, useModal } from '@billy/design-system'
import type { HashHistory, Transition } from 'history'
import React, { createContext, ReactNode, useCallback, useContext, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { BackButtonBlockerId } from '../enums/backButtonBlockerId'
import { CustomEvent } from '../enums/customEvent'
import { ModalId } from '../enums/modalId'
import { useCustomEventListener } from '../hooks/useCustomEventListener'
import { BrowserBackButtonBlockRequest } from '../types/BrowserBackButtonBlockRequest'
import { RegisterEvent } from '../types/registerEvent'
import { dispatchCustomEvent } from '../utils/customEvent'

interface ContextState {
  setDirty: (isDirty: boolean) => void
}

const DirtyRouteContext = createContext<ContextState | undefined>(undefined)

export type SetDirtyEvent = RegisterEvent<{
  isDirty: boolean
  onRouteChange: () => void
  onRouteBlocked: () => void
}>

export interface DirtyRouteProps {
  onLeaveRoute: (event: RegisterEvent<undefined>) => void
  onSetDirtyRouteFlag: (event: SetDirtyEvent) => void
}

interface DirtyRouteContextProviderProps extends DirtyRouteProps {
  children: ReactNode
  // The hash-history instance backing this area's own <HistoryRouter> (see useHashHistory.ts,
  // Settings.tsx/Umbrella.tsx) - only views mounted under their own hash router need to guard
  // in-router navigation (e.g. switching tabs) separately from the browser Back/Forward button,
  // which the app's shared history singleton already guards via BlockBrowserBackButtonRequested.
  hashHistory?: HashHistory
}

export const DirtyRouteContextProvider = ({
  children,
  onLeaveRoute,
  onSetDirtyRouteFlag,
  hashHistory,
}: DirtyRouteContextProviderProps) => {
  const { close: closeModal, open: openModal } = useModal(ModalId.DirtyRouteModal)
  const { t } = useTranslation()
  const navigate = useNavigate()
  const isDirty = useRef(false)
  const pendingHashTransition = useRef<Transition>()
  const shouldUseHistoryBack = useRef(false)
  const isModalOpen = useRef(false)
  const unblockHashHistoryRef = useRef<() => void>(() => undefined)

  const handleBlockHashHistory = useCallback(
    (tx: Transition) => {
      if (isDirty.current) {
        pendingHashTransition.current = tx
        isModalOpen.current = true
        openModal()
        return
      }

      closeModal()
      tx.retry()
    },
    [closeModal, openModal],
  )

  // Only held registered while genuinely dirty - history.block() forces every POP within this
  // hash router through a revert-then-ask round trip as soon as ANY blocker is registered, even
  // ones that end up allowing the transition instantly. Registering this unconditionally for the
  // component's whole mounted lifetime would introduce a visible address-bar bounce for this hash
  // router's own tab switches (the same mechanism the outer shared history has, guarded the same
  // way in useReactEmberRouterConnection.ts).
  const syncHashBlockRegistration = useCallback(
    (shouldBlock: boolean) => {
      if (!hashHistory) {
        return
      }

      unblockHashHistoryRef.current()
      unblockHashHistoryRef.current = shouldBlock ? hashHistory.block(handleBlockHashHistory) : () => undefined
    },
    [hashHistory, handleBlockHashHistory],
  )

  useEffect(() => {
    return () => {
      unblockHashHistoryRef.current()
      pendingHashTransition.current = undefined
    }
  }, [])

  const handleRouteChange = useCallback(() => {
    closeModal()
    isModalOpen.current = false
  }, [closeModal])

  const handleRouteBlocked = useCallback(() => {
    shouldUseHistoryBack.current = false
    openModal()
    isModalOpen.current = true
  }, [openModal])

  const handleSetDirty = useCallback(
    (newIsDirty: boolean) => {
      isDirty.current = newIsDirty
      syncHashBlockRegistration(newIsDirty)
      dispatchCustomEvent<BrowserBackButtonBlockRequest>(CustomEvent.BlockBrowserBackButtonRequested, {
        sourceId: BackButtonBlockerId.DirtyRouteContext,
        value: isDirty.current,
      })

      onSetDirtyRouteFlag({
        detail: { isDirty: isDirty.current, onRouteBlocked: handleRouteBlocked, onRouteChange: handleRouteChange },
      })
    },
    [handleRouteBlocked, handleRouteChange, onSetDirtyRouteFlag, syncHashBlockRegistration],
  )

  const handleLeaveRoute = useCallback(() => {
    handleSetDirty(false)
    closeModal()
    isModalOpen.current = false

    if (shouldUseHistoryBack.current) {
      navigate(-1)
    } else if (pendingHashTransition.current) {
      pendingHashTransition.current.retry()
      pendingHashTransition.current = undefined
    } else {
      onLeaveRoute({ detail: undefined })
    }
  }, [closeModal, handleSetDirty, navigate, onLeaveRoute])

  useCustomEventListener(CustomEvent.BrowserNavigationButtonClickedAndBlocked, () => {
    if (isModalOpen.current && !hashHistory) {
      shouldUseHistoryBack.current = true
    }
  })

  return (
    <DirtyRouteContext.Provider
      value={{
        setDirty: handleSetDirty,
      }}
    >
      {children}
      <ModalConfirmation
        cancelLabel={t('dirty_route_modal.cancel_button')}
        danger
        id={ModalId.DirtyRouteModal}
        message={t('dirty_route_modal.content')}
        okLabel={t('dirty_route_modal.ok_button')}
        onOk={handleLeaveRoute}
        title={t('dirty_route_modal.title')}
      />
    </DirtyRouteContext.Provider>
  )
}

export const useDirtyRoute = () => {
  const context = useContext(DirtyRouteContext)

  if (!context) {
    throw new Error('DirtyRouteContextProvider is missing in the module!')
  }

  return context
}
